// Posting — date helpers with one local "today" (Oct 18, 2026). Nothing in the
// Posting mock may be dated after it. Pure functions, no imports from the app.

export const POSTING_TODAY_ISO = '2026-10-18';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'] as const;

export type DatePreset = 'this-month' | 'last-month' | 'last-90' | 'this-year' | 'all-time' | 'custom';

export const DATE_PRESET_LABELS: { value: DatePreset; label: string }[] = [
  { value: 'this-month', label: 'This month' },
  { value: 'last-month', label: 'Last month' },
  { value: 'last-90', label: 'Last 90 days' },
  { value: 'this-year', label: 'This year' },
  { value: 'all-time', label: 'All time' },
  { value: 'custom', label: 'Custom range' },
];

export interface IsoRange {
  /** Inclusive start (yyyy-mm-dd) or null for open-ended. */
  from: string | null;
  to: string | null;
}

function utc(iso: string): Date {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(Date.UTC(y ?? 2026, (m ?? 1) - 1, d ?? 1, 12));
}
function toIso(d: Date): string {
  return d.toISOString().slice(0, 10);
}

export function addDays(iso: string, n: number): string {
  const d = utc(iso);
  d.setUTCDate(d.getUTCDate() + n);
  return toIso(d);
}

/** 0 = Sunday … 6 = Saturday. */
export function weekday(iso: string): number {
  return utc(iso).getUTCDay();
}
export function isWeekend(iso: string): boolean {
  const w = weekday(iso);
  return w === 0 || w === 6;
}

/** Business days after `a` up to and including `b` (a → b). */
export function businessDaysBetween(a: string, b: string): number {
  let n = 0;
  let cur = a;
  while (cur < b) {
    cur = addDays(cur, 1);
    if (!isWeekend(cur)) n += 1;
  }
  return n;
}

/** The date `n` business days before `iso`. */
export function subBusinessDays(iso: string, n: number): string {
  let cur = iso;
  let left = n;
  while (left > 0) {
    cur = addDays(cur, -1);
    if (!isWeekend(cur)) left -= 1;
  }
  return cur;
}

/** "Oct 14" */
export function shortDate(iso: string): string {
  const d = utc(iso);
  return `${MONTHS[d.getUTCMonth()] ?? 'Jan'} ${String(d.getUTCDate())}`;
}
/** "Oct 14, 2026" */
export function longDate(iso: string): string {
  const d = utc(iso);
  return `${shortDate(iso)}, ${String(d.getUTCFullYear())}`;
}
/** "OCT 14" (group label in mono). */
export function monoDay(iso: string): string {
  return shortDate(iso).toUpperCase();
}
/** "Oct 1–14" (same month) or "Oct 28 – Nov 3". */
export function spanLabel(fromIso: string, toIso_: string): string {
  const a = utc(fromIso);
  const b = utc(toIso_);
  if (fromIso === toIso_) return shortDate(fromIso);
  if (a.getUTCMonth() === b.getUTCMonth()) return `${shortDate(fromIso)}–${String(b.getUTCDate())}`;
  return `${shortDate(fromIso)} – ${shortDate(toIso_)}`;
}
/** "Oct 1 – Oct 18, 2026" for the date chip. */
export function chipLabel(range: IsoRange): string {
  if (!range.from || !range.to) return 'All time';
  const y = String(utc(range.to).getUTCFullYear());
  return `${shortDate(range.from)} – ${shortDate(range.to)}, ${y}`;
}

/** The {from,to} window for a preset, measured from the Posting "today". */
export function presetRange(preset: DatePreset, custom?: IsoRange): IsoRange {
  const t = utc(POSTING_TODAY_ISO);
  const y = t.getUTCFullYear();
  const m = t.getUTCMonth();
  switch (preset) {
    case 'this-month':
      return { from: toIso(new Date(Date.UTC(y, m, 1, 12))), to: POSTING_TODAY_ISO };
    case 'last-month':
      return { from: toIso(new Date(Date.UTC(y, m - 1, 1, 12))), to: toIso(new Date(Date.UTC(y, m, 0, 12))) };
    case 'last-90':
      return { from: addDays(POSTING_TODAY_ISO, -90), to: POSTING_TODAY_ISO };
    case 'this-year':
      return { from: `${String(y)}-01-01`, to: POSTING_TODAY_ISO };
    case 'all-time':
      return { from: null, to: null };
    case 'custom':
      return custom ?? { from: null, to: null };
  }
}

/** Which preset a {from,to} pair is (custom when none matches). */
export function presetOf(range: IsoRange): DatePreset {
  const order: DatePreset[] = ['this-month', 'last-month', 'last-90', 'this-year', 'all-time'];
  for (const p of order) {
    const r = presetRange(p);
    if (r.from === range.from && r.to === range.to) return p;
  }
  return 'custom';
}

export function inRange(iso: string, range: IsoRange): boolean {
  if (range.from && iso < range.from) return false;
  if (range.to && iso > range.to) return false;
  return true;
}

/** Words for a window in a sentence: "Oct 1 and Oct 18, 2026" style pieces. */
export function windowWords(range: IsoRange): { from: string; to: string } {
  if (!range.from || !range.to) return { from: 'the first statement', to: shortDate(POSTING_TODAY_ISO) };
  return { from: shortDate(range.from), to: shortDate(range.to) };
}

/** Current local time as "11:42 AM" (12-hour). */
export function nowClock(): string {
  const d = new Date();
  const h = d.getHours();
  const hr = ((h + 11) % 12) + 1;
  return `${String(hr)}:${String(d.getMinutes()).padStart(2, '0')} ${h < 12 ? 'AM' : 'PM'}`;
}

/** ISO of a "Jul 4" style label in the demo year. */
export function isoFromLabel(label: string, year = 2026): string {
  const m = /^([A-Z][a-z]{2}) (\d{1,2})/.exec(label);
  const idx = MONTHS.indexOf((m?.[1] ?? 'Jan') as (typeof MONTHS)[number]);
  return `${String(year)}-${String(idx + 1).padStart(2, '0')}-${(m?.[2] ?? '1').padStart(2, '0')}`;
}
